import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo, useRef } from 'react';
import { loadIndex } from '../lib/index-loader';
import { getNumericSynonymSuggestions } from '../lib/synonym-expander';
import { useSearchEngine } from '../hooks/useSearchEngine';
import { getI18n } from '../lib/i18n';
const SearchBoxApp = ({ placeholder }) => {
    const i18n = getI18n();
    const resolvedPlaceholder = placeholder || i18n.placeholder;
    const [products, setProducts] = useState([]);
    const [synonyms, setSynonyms] = useState({});
    const [indexLoaded, setIndexLoaded] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [query, setQuery] = useState('');
    const [includeBStock, setIncludeBStock] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const suggestions = window.enoSearchConfig?.suggestions || [];
    const enabledFields = window.enoSearchConfig?.enabledFields;
    const maxResults = window.enoSearchConfig?.maxResults;
    const salesBoost = window.enoSearchConfig?.salesBoost;
    const { search, isReady } = useSearchEngine(products, synonyms, enabledFields, maxResults, salesBoost);
    // Load index on mount
    useEffect(() => {
        const config = window.enoSearchConfig;
        if (!config)
            return;
        loadIndex(config)
            .then(({ products: p, synonyms: s }) => {
            setProducts(p);
            setSynonyms(s);
            setIndexLoaded(true);
        })
            .catch(err => {
            setLoadError(err instanceof Error ? err.message : 'Failed to load search index');
        });
    }, []);
    const results = useMemo(() => {
        if (!query.trim() || !isReady)
            return [];
        const raw = search(query);
        if (includeBStock)
            return raw;
        return raw.filter(r => !r.product.sku.toUpperCase().startsWith('BST-'));
    }, [query, search, isReady, includeBStock]);
    const hasQuery = query.trim().length > 0;
    const isShortQuery = query.trim().length < 3;
    // Surface cable model numbers (e.g. "2" -> "2524", "2534", ...) registered under
    // numeric synonym keys as extra suggestion chips while the user types.
    const dynamicSuggestions = useMemo(() => getNumericSynonymSuggestions(query, synonyms), [query, synonyms]);
    const displaySuggestions = dynamicSuggestions.length > 0
        ? [...dynamicSuggestions, ...suggestions]
        : suggestions;
    // Show suggestion chips while focused, the index is ready, and the query
    // is too short to search or returned no results.
    const showSuggestions = isFocused && indexLoaded && !loadError
        && displaySuggestions.length > 0 && (isShortQuery || results.length === 0 || dynamicSuggestions.length > 0);
    const isOpen = isFocused && (hasQuery || showSuggestions);
    // Reset keyboard navigation when query changes
    useEffect(() => {
        setActiveIndex(0);
    }, [query]);
    // Close on click outside
    useEffect(() => {
        const handleClick = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);
    // Keyboard navigation
    useEffect(() => {
        const handleKey = (e) => {
            if (!isOpen)
                return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActiveIndex(i => Math.min(i + 1, results.length - 1));
            }
            else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActiveIndex(i => Math.max(i - 1, 0));
            }
            else if (e.key === 'Enter' && results[activeIndex]) {
                window.location.href = results[activeIndex].product.url;
            }
            else if (e.key === 'Escape') {
                setIsFocused(false);
                inputRef.current?.blur();
            }
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, results, activeIndex]);
    const handleSelect = (result) => {
        window.location.href = result.product.url;
    };
    const handleSuggestionClick = (suggestion) => {
        setQuery(suggestion);
        inputRef.current?.focus();
    };
    const resultCount = results.length;
    const resultLabel = resultCount === 1 ? i18n.resultSingular : i18n.resultPlural;
    return (_jsxs("div", { className: "eno-search-box", ref: containerRef, children: [_jsxs("div", { className: "eno-search-box-input-wrapper", children: [_jsx("input", { ref: inputRef, type: "search", className: "eno-search-box-input", placeholder: resolvedPlaceholder, value: query, onChange: e => setQuery(e.target.value), onFocus: () => setIsFocused(true), "aria-label": i18n.searchLabel, "aria-autocomplete": "list", "aria-expanded": isOpen, autoComplete: "off" }), _jsx("span", { className: "eno-search-box-icon", "aria-hidden": "true", children: _jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })] }) })] }), isOpen && (_jsxs("div", { className: "eno-search-box-dropdown", children: [showSuggestions && (_jsxs("div", { className: "eno-search-box-suggestions-wrap", children: [_jsx("div", { className: "eno-search-box-suggestions-label", children: i18n.suggestions }), _jsx("div", { className: "eno-search-box-suggestions", children: displaySuggestions.map((suggestion, idx) => (_jsx("button", { type: "button", className: "eno-search-box-suggestion", onClick: () => handleSuggestionClick(suggestion), children: suggestion }, idx))) })] })), loadError ? (_jsxs("div", { className: "eno-search-box-state", children: [_jsx("strong", { children: i18n.errorTitle }), _jsx("p", { children: loadError })] })) : !indexLoaded ? (_jsx("div", { className: "eno-search-box-state eno-search-box-loading", children: i18n.loadingIndex })) : results.length === 0 ? (hasQuery && (_jsx("div", { className: "eno-search-box-state", children: query.trim().length < 3 ? i18n.shortQueryHint : i18n.noResultsFor.replace('%s', query) }))) : (_jsx("ul", { className: "eno-search-box-results", role: "listbox", children: results.map((result, idx) => (_jsxs("li", { className: `eno-search-box-result-item${idx === activeIndex ? ' active' : ''}`, role: "option", "aria-selected": idx === activeIndex, onClick: () => handleSelect(result), onMouseEnter: () => setActiveIndex(idx), children: [result.product.image ? (_jsx("img", { src: result.product.image, alt: result.product.title, className: "eno-search-box-thumb", loading: "lazy" })) : (_jsx("div", { className: "eno-search-box-thumb eno-search-box-thumb--empty" })), _jsxs("div", { className: "eno-search-box-result-content", children: [_jsx("div", { className: "eno-search-box-result-title", children: result.product.title }), result.product.price && (_jsx("div", { className: "eno-search-box-result-price", dangerouslySetInnerHTML: { __html: result.product.price } }))] })] }, result.product.id))) })), _jsxs("div", { className: "eno-search-box-footer", children: [_jsxs("label", { className: "eno-search-box-bstock-toggle", children: [_jsx("input", { type: "checkbox", checked: includeBStock, onChange: e => setIncludeBStock(e.target.checked) }), _jsx("span", { children: i18n.includeBStock })] }), indexLoaded && !loadError && hasQuery && (_jsxs("span", { className: "eno-search-box-count", children: [resultCount, " ", resultLabel] }))] })] }))] }));
};
export default SearchBoxApp;
